// @system — Client-side Sentry error tracking initialization
// Mirrors the server ErrorTracking module pattern: lazy init, graceful fallback.
// Call initSentry() once at app startup (main.jsx); it returns a promise but React does not
// need to wait for it — errors thrown before the SDK is up are still caught by ErrorBoundary
// and reported once it is (captureException falls back to window.Sentry).
//
// The SDK is loaded as its own async chunk (`vendor-sentry`) ONLY when a DSN is configured. It
// is ~100 KB gzipped — a third of the landing page's first-load script budget — and many
// products never set a DSN, so shipping it in the initial bundle penalised every visitor for a
// feature that was off (Lighthouse perf gate, docs/SCALABILITY.md §8).
//
// DSN resolution order:
//   1. VITE_ERROR_TRACKING_DSN env var (baked at build time by webpack DefinePlugin)
//   2. VITE_SENTRY_DSN env var (alias)
//   3. <meta name="sentry-dsn"> tag (injected at runtime by the server —
//      server/src/lib/@system/runtimeHtml.js — when it serves index.html)
//
// Release resolution order (the same "<VERSION>+<sha>" string the server reports
// in /api/health `release`, process_build_info and its own Sentry release):
//   1. VITE_APP_VERSION env var (build time)
//   2. <meta name="app-release"> tag — runtimeHtml injects VERSION, which the
//      Dockerfile stamped as "<semver>+<short sha>" (docs/MONITORING.md)
//
// In production Docker builds the VITE_* env vars are typically NOT available at
// build time. The meta tag fallbacks (#36046) ensure DSN and release are injected
// at runtime by the server, so Sentry initializes without requiring a rebuild.

/** @type {typeof import('@sentry/react') | null} */
let sdk = null
let initialized = false

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const EMAIL_CENSOR = '[email]'
const SENSITIVE_HEADERS = ['authorization', 'proxy-authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-csrf-token', 'x-xsrf-token']
const MAX_DEPTH = 8

/**
 * Read a <meta name="..."> content value, ignoring runtime placeholders
 * ("__SENTRY_DSN__", "__APP_RELEASE__") left in place by a dev build.
 * @param {string} name
 * @returns {string | null}
 */
function readMeta(name) {
  if (typeof document === 'undefined') return null
  const content = document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')
  if (!content || content.startsWith('__')) return null
  return content
}

/**
 * Read Sentry DSN from the <meta name="sentry-dsn"> tag injected by the server (runtimeHtml).
 * @returns {string | null}
 */
function getDsnFromMeta() {
  return readMeta('sentry-dsn')
}

/**
 * Resolve the DSN from build-time env or the runtime meta tag.
 * @returns {string | null}
 */
export function resolveSentryDsn() {
  return import.meta.env.VITE_ERROR_TRACKING_DSN || import.meta.env.VITE_SENTRY_DSN || getDsnFromMeta() || null
}

/**
 * Release string for this build: VITE_APP_VERSION, else the runtime-injected
 * <meta name="app-release">, else null (unknown build).
 * @returns {string | null}
 */
export function getRelease() {
  return import.meta.env.VITE_APP_VERSION || readMeta('app-release') || null
}

/**
 * Performance tracing sample rate: VITE_SENTRY_TRACES_SAMPLE_RATE clamped to
 * [0, 1], default 0 (errors only — tracing costs quota on every page load).
 * @param {unknown} raw
 * @returns {number}
 */
export function parseSampleRate(raw) {
  if (raw === undefined || raw === null || raw === '') return 0
  const n = Number(raw)
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

/**
 * @param {unknown} v
 * @returns {v is Record<string, unknown>}
 */
function isObject(v) {
  return typeof v === 'object' && v !== null
}

/**
 * Recursively mask e-mail addresses inside any JSON-like value (returns a copy).
 * @param {unknown} value
 * @param {number} [depth]
 * @param {WeakSet<object>} [seen]
 * @returns {unknown}
 */
function scrubValue(value, depth = 0, seen = new WeakSet()) {
  if (typeof value === 'string') return value.replace(EMAIL_RE, EMAIL_CENSOR)
  if (!isObject(value) || depth >= MAX_DEPTH) return value
  if (seen.has(value)) return undefined
  seen.add(value)
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1, seen))
  /** @type {Record<string, unknown>} */
  const out = {}
  for (const [k, v] of Object.entries(value)) out[k] = scrubValue(v, depth + 1, seen)
  return out
}

/**
 * Sentry `beforeSend`: drop credential headers and cookies, mask e-mail
 * addresses everywhere, remove user.email / user.ip_address. Never returns null.
 * Mirrors server/src/lib/@system/ErrorTracking/scrub.js. Non-object input
 * (null, a primitive) is returned untouched.
 * @template E
 * @param {E} event
 * @returns {E}
 */
export function scrubEvent(event) {
  if (!isObject(event)) return event
  /** @type {Record<string, unknown>} */
  const out = { ...event }
  if (isObject(out.request)) {
    /** @type {Record<string, unknown>} */
    const request = { ...out.request }
    if (isObject(request.headers)) {
      request.headers = Object.fromEntries(
        Object.entries(request.headers).filter(([k]) => !SENSITIVE_HEADERS.includes(k.toLowerCase())),
      )
    }
    delete request.cookies
    if (request.data !== undefined) request.data = scrubValue(request.data)
    out.request = request
  }
  if (isObject(out.user)) {
    /** @type {Record<string, unknown>} */
    const user = { ...out.user }
    delete user.email
    delete user.ip_address
    out.user = scrubValue(user)
  }
  for (const key of ['message', 'exception', 'breadcrumbs', 'extra', 'contexts', 'tags']) {
    if (out[key] !== undefined) out[key] = scrubValue(out[key])
  }
  return /** @type {E} */ (out)
}

/**
 * Build the Sentry.init() options (pure; unit-tested). `sdk` is the lazily
 * imported @sentry/react module — without it no integrations are attached
 * (tests build options without loading the SDK).
 * @param {{ dsn: string, prod?: boolean, release?: string | null, tracesSampleRate?: unknown, sdk?: typeof import('@sentry/react') | null }} input
 * @returns {Record<string, any>}
 */
export function buildSentryOptions({ dsn, prod = import.meta.env.PROD, release = getRelease(), tracesSampleRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, sdk: sentrySdk = null }) {
  return {
    dsn,
    environment: prod ? 'production' : 'development',
    release: release || undefined,
    enabled: prod,
    tracesSampleRate: parseSampleRate(tracesSampleRate),
    // Replay disabled — opt-in only
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: sentrySdk ? [sentrySdk.browserTracingIntegration()] : [],
    beforeSend: scrubEvent,
  }
}

/**
 * Initialize the Sentry browser SDK when a DSN is configured. Loads @sentry/react on demand
 * (async chunk `vendor-sentry`) and sets window.Sentry so legacy callers can still reach
 * captureException.
 * @returns {Promise<boolean>} true when the SDK was initialised
 */
export async function initSentry() {
  const dsn = resolveSentryDsn()
  if (!dsn) return false

  try {
    const Sentry = await import(/* webpackChunkName: "vendor-sentry" */ '@sentry/react')
    Sentry.init(buildSentryOptions({ dsn, sdk: Sentry }))
    sdk = Sentry
    // Expose on window for backwards compatibility (window.Sentry.captureException)
    window.Sentry = Sentry
    initialized = true
    return true
  } catch (err) {
    console.warn('[Sentry] Failed to initialize:', err instanceof Error ? err.message : String(err))
    return false
  }
}

/**
 * Report an exception (ErrorBoundary, async handlers). No-op when Sentry is not
 * configured; falls back to a window.Sentry instance when one was provided by
 * the host page.
 * @param {unknown} error
 * @param {Record<string, any>} [context] Sentry capture context (extra, tags, level…)
 * @returns {string | null} event id when sent
 */
export function captureException(error, context) {
  if (initialized && sdk) return sdk.captureException(error, context)
  if (typeof window !== 'undefined' && window.Sentry?.captureException) return window.Sentry.captureException(error, context) ?? null
  return null
}

export function isSentryEnabled() {
  return initialized
}
